import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';
import { supabase } from '../../../lib/supabase';

const ConvertQuoteModal = ({ isOpen, onClose, quote, onConfirm }) => {
  const [conversionData, setConversionData] = useState({
    serviceTypeId: '',
    priorityId: '',
    notes: ''
  });

  const [serviceTypes, setServiceTypes] = useState([]);
  const [priorities, setPriorities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isConverting, setIsConverting] = useState(false);

  useEffect(() => {
    if (isOpen) {
      fetchConversionData();
    }
  }, [isOpen]);

  const fetchConversionData = async () => {
    try {
      setLoading(true);

      // Fetch service types
      const { data: serviceTypesData } = await supabase
        ?.from('ticket_service_types')
        ?.select('id, name, description')
        ?.eq('is_active', true)
        ?.order('display_order');

      setServiceTypes((serviceTypesData || [])?.map(st => ({
        value: st?.id,
        label: st?.name,
        description: st?.description || ''
      })));

      // Fetch priorities
      const { data: prioritiesData } = await supabase
        ?.from('ticket_priorities')
        ?.select('id, name, description')
        ?.eq('is_active', true)
        ?.order('display_order');

      setPriorities((prioritiesData || [])?.map(p => ({
        value: p?.id,
        label: p?.name,
        description: p?.description || ''
      })));
    } catch (error) {
      console.error('Error fetching conversion data:', error?.message);
    } finally {
      setLoading(false);
    }
  };

  const handleConvert = async () => {
    if (!conversionData?.serviceTypeId || !conversionData?.priorityId) {
      alert('Please select a service type and priority');
      return;
    }

    try {
      setIsConverting(true);
      await onConfirm(conversionData);
      handleClose();
    } catch (error) {
      console.error('Error converting quote:', error);
      alert(error?.message || 'Failed to convert quote to ticket. Please try again.');
    } finally {
      setIsConverting(false);
    }
  };

  const handleClose = () => {
    setConversionData({
      serviceTypeId: '',
      priorityId: '',
      notes: ''
    });
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-[2000] flex items-center justify-center p-4">
      <div className="bg-card rounded-xl shadow-elevation-5 w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-card border-b border-border p-6 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-success/10 rounded-lg flex items-center justify-center">
              <Icon name="ArrowRight" size={20} color="var(--color-success)" />
            </div>
            <div>
              <h2 className="text-xl font-heading font-semibold text-foreground">Convert Quote to Ticket</h2>
              <p className="text-sm text-muted-foreground">Quote: {quote?.quote_number}</p>
            </div>
          </div>
          <button
            onClick={handleClose}
            className="p-2 rounded-lg hover:bg-muted transition-smooth"
          >
            <Icon name="X" size={20} color="var(--color-foreground)" />
          </button>
        </div>

        <div className="p-6 space-y-6">
          <div className="bg-muted/30 rounded-lg p-4">
            <h3 className="text-sm font-caption font-medium text-muted-foreground mb-3">Quote Summary</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <p className="text-xs text-muted-foreground mb-1">Customer</p>
                <p className="font-medium text-foreground">{quote?.customer?.name}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground mb-1">Total Amount</p>
                <p className="font-medium text-foreground">${parseFloat(quote?.total_amount || 0)?.toFixed(2)}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground mb-1">Products/Services</p>
                <p className="font-medium text-foreground">{quote?.quote_products?.length} items</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground mb-1">Salesperson</p>
                <p className="font-medium text-foreground">{quote?.salesperson?.name}</p>
              </div>
            </div>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Icon name="Loader2" size={24} className="animate-spin text-primary" />
              <span className="ml-3 text-muted-foreground">Loading form...</span>
            </div>
          ) : (
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <Select
                  label="Service Type"
                  placeholder="Select service type"
                  required
                  searchable
                  options={serviceTypes}
                  value={conversionData?.serviceTypeId}
                  onChange={(value) => setConversionData(prev => ({ ...prev, serviceTypeId: value }))}
                />

                <Select
                  label="Priority"
                  placeholder="Select priority"
                  required
                  options={priorities}
                  value={conversionData?.priorityId}
                  onChange={(value) => setConversionData(prev => ({ ...prev, priorityId: value }))}
                />
              </div>

              <div className="space-y-2">
                <label className="block text-sm font-medium text-foreground">
                  Additional Notes
                </label>
                <textarea
                  className="w-full min-h-[100px] px-4 py-3 bg-background border border-input rounded-lg text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent resize-y transition-smooth"
                  placeholder="Add any special instructions or notes..."
                  value={conversionData?.notes}
                  onChange={(e) => setConversionData(prev => ({ ...prev, notes: e?.target?.value }))}
                />
                <p className="text-xs text-muted-foreground">Optional</p>
              </div>
            </div>
          )}

          <div className="bg-warning/5 border border-warning/20 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <Icon name="Clock" size={20} color="var(--color-warning)" className="flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-sm font-medium text-foreground mb-1">Pending — Awaiting Scheduling</p>
                <p className="text-sm text-muted-foreground">
                  This ticket will be created with <strong>Pending</strong> status. When the customer calls to schedule the work, the manager will assign technicians and set a date — the status will automatically change to <strong>Scheduled</strong>.
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="sticky bottom-0 bg-card border-t border-border p-6 flex flex-col sm:flex-row gap-3">
          <Button
            variant="outline"
            onClick={handleClose}
            iconName="X"
            iconPosition="left"
            className="flex-1"
          >
            Cancel
          </Button>
          <Button
            variant="success"
            onClick={handleConvert}
            loading={isConverting}
            disabled={
              !conversionData?.serviceTypeId ||
              !conversionData?.priorityId
            }
            iconName="CheckCircle"
            iconPosition="right"
            className="flex-1"
          >
            {isConverting ? 'Converting...' : 'Convert to Ticket'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ConvertQuoteModal;